import { Col, Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Layout } from '@/app/components/Layout/Layout'
import { Motion } from '@/app/components/Motion/Motion'
import { Spinner } from '@/app/components/Spinner/Spinner'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import { useNavigate } from '@/app/hooks/useNavigate'
import { useSession } from '@/app/state/session'
import { fromISODate, fromISOTime, toISODate, toISOTime } from '@/app/utils/date'
import { parseError } from '@/app/utils/error'
import type { FunctionalComponent } from 'preact'
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { IMask, IMaskInput } from 'react-imask'
import { useShallow } from 'zustand/shallow'

export const SettingsPage: FunctionalComponent<{
  path?: string
}> = () => {
  const route = useNavigate()

  const profile = useSession(useShallow(s => ({
    userName: s.profile?.userName,
    birthDate: s.profile?.birthDate,
    birthTime: s.profile?.birthTime,
    locationName: s.profile?.location?.name_ru || s.profile?.location?.name_en,
  })))

  const patchProfile = useSession(s => s.patchProfile)
  const user = useSession(useShallow(s => s.user))
  const [errors, setErrors] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [birthTime, setBirthTime] = useState('')
  const [userName, setUserName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [locationName, setLocationName] = useState('')
  const [pendingNavigation, setPendingNavigation] = useState<null | (() => void)>(null)
  const back = useBack('~/tools')

  const dateValid = /^\d{2}\.\d{2}\.\d{4}$/.test(birthDate)
  const timeValid = birthTime ? /^\d{2}\:\d{2}$/.test(birthTime) : true

  const valid = dateValid && timeValid && !!userName

  const initialSettings = useMemo(() => ({
    userName: profile?.userName ?? '',
    birthDate: profile?.birthDate ? fromISODate(profile.birthDate) : '',
    birthTime: profile?.birthTime ? fromISOTime(profile.birthTime) : '',
    locationName: profile.locationName ?? '',
  }), [profile, user])

  const dirty = userName !== initialSettings.userName
    || birthDate !== initialSettings.birthDate
    || birthTime !== initialSettings.birthTime
    || locationName !== initialSettings.locationName

  const saveSettings = useCallback(async () => {
    try {
      setErrors([])
      setLoading(true)

      await patchProfile({
        profile: {
          userName,
          birthDate: toISODate(birthDate),
          birthTime: toISOTime(birthTime),
        },
      })

      return true
    }
    catch (e) {
      setErrors(parseError(e))
      console.error(e)
      return false
    }
    finally {
      setLoading(false)
    }
  }, [birthDate, birthTime, patchProfile, userName])

  const confirmLeave = useCallback((next: () => void) => {
    if (!dirty) {
      next()
      return
    }

    setPendingNavigation(() => next)
  }, [dirty])

  useEffect(() => {
    setUserName(profile?.userName ?? '')
    setBirthDate(profile?.birthDate ? fromISODate(profile.birthDate) : '')
    setBirthTime(profile?.birthTime ? fromISOTime(profile.birthTime) : '')
    setLocationName(profile.locationName ?? '')
  }, [profile])

  useEffect(() => {
    if (!dirty) return

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', onBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [dirty])

  return (
    <Layout>
      {pendingNavigation && (
        <UI.Popup
          onClose={() => {
            setPendingNavigation(null)
          }}
        >
          <Lib.PopupHeader>
            Есть изменения
          </Lib.PopupHeader>

          <Lib.PopupMessage>
            Сохранить изменения перед переходом?
          </Lib.PopupMessage>

          <Col grid gap={15}>
            <UI.Button
              size='m'
              type='secondary'
              htmlType='button'
              disabled={loading}
              onClick={() => {
                const next = pendingNavigation
                setPendingNavigation(null)
                next()
              }}
            >
              Нет
            </UI.Button>

            <UI.Button
              type='primary'
              size='m'
              htmlType='button'
              disabled={!valid || loading}
              onClick={async () => {
                const next = pendingNavigation
                const saved = await saveSettings()

                if (saved) {
                  setPendingNavigation(null)
                  next()
                }
              }}
            >
              Сохранить
              {loading && <Spinner size={20} />}
            </UI.Button>
          </Col>
        </UI.Popup>
      )}

      <Layout.Content
        help
        logout
        onBack={() => {
          confirmLeave(back)
        }}
      >
        <Motion>
          <Layout.Header>
            Профиль
          </Layout.Header>

          <form>
            <Row gap={15}>
              <Lib.Fields>
                {user?.email && (
                  <Input.Block label='Email'>
                    <Input
                      disabled
                      readOnly
                      value={user.email}
                    />
                  </Input.Block>
                )}

                <Input.Block label='Имя'>
                  <Input
                    disabled={loading}
                    value={userName}
                    onChange={setUserName}
                    onInput={setUserName}
                  />
                </Input.Block>

                <Input.Block label='Дата рождения'>
                  <Input
                    disabled={loading}
                    type='tel'
                    placeholder='DD.MM.YYYY'
                    value={birthDate}
                    onInput={setBirthDate}
                    onChange={setBirthDate}
                  >
                    {props => (
                      <IMaskInput
                        {...props}
                        overwrite
                        type='tel'
                        mask={Date}
                        min={new Date(1900, 0, 1)}
                        max={new Date(2050, 0, 1)}
                      />
                    )}
                  </Input>
                </Input.Block>

                <Input.Block label='Время рождения'>
                  <Input
                    disabled={loading}
                    type='tel'
                    placeholder='HH:MM'
                    value={birthTime}
                    onInput={setBirthTime}
                    onChange={setBirthTime}
                  >
                    {props => (
                      <IMaskInput
                        {...props}
                        overwrite
                        type='tel'
                        mask={Date}
                        pattern='H:M'
                        blocks={{
                          H: {
                            mask: IMask.MaskedRange,
                            from: 0,
                            to: 23,
                            maxLength: 2,
                          },
                          M: {
                            mask: IMask.MaskedRange,
                            from: 0,
                            to: 59,
                            maxLength: 2,
                          },
                        }}
                        format={(date: Date | null) => {
                          if (!date) return ''
                          const hours = String(date.getHours()).padStart(2, '0')
                          const minutes = String(date.getMinutes()).padStart(2, '0')
                          return `${hours}:${minutes}`
                        }}
                        parse={(value: string) => {
                          if (!value) return null
                          const [hours, minutes] = value.split(':').map(Number)
                          if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
                          const date = new Date()
                          date.setHours(hours, minutes, 0, 0)
                          return date
                        }}
                      />
                    )}
                  </Input>
                </Input.Block>

                <Input.Block label='Место рождения'>
                  <Input
                    readOnly
                    showArrow
                    placeholder='Выбери город'
                    value={locationName}
                    onClick={() => {
                      confirmLeave(() => {
                        route('/settings/location')
                      })
                    }}
                  />
                </Input.Block>

                <Lib.Errors errors={errors} />
              </Lib.Fields>

              <UI.Button
                size='l'
                type='primary'
                htmlType='button'
                disabled={!valid || loading}
                onClick={async () => {
                  const saved = await saveSettings()
                  if (saved) {
                    back()
                  }
                }}
              >
                Сохранить
              </UI.Button>
            </Row>
          </form>
        </Motion>
      </Layout.Content>
    </Layout>
  )
}
